import { SyntheticEvent, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { useShell } from '@/providers';
import { useCurrentOrg } from '@/hooks/useCurrentOrg';
import { useIsAbriStaff } from '@/hooks/useIsAbriStaff';
import { useRegistryRoutes } from '@/hooks/useRegistryRoutes';
import {
  AdjustmentsVerticalIcon,
  AnimalsIcon,
  LeadingIcon,
} from '@/components/icons';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import HistoryIcon from '@mui/icons-material/History';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { navigationMessages } from './messages';
import ShellNavItems from './ShellNavItems';
import { Box } from '@/primitives';

export default function ShellSidebarLinks() {
  const pathname = usePathname();
  const isAbriStaff = useIsAbriStaff();
  const { isAdmin } = useCurrentOrg();
  const canViewAudit = isAbriStaff;
  const canImportData = isAbriStaff;
  const canManageSocietySettings = isAdmin;
  const registryRoutes = useRegistryRoutes();
  const { configMenuOpen, sidebarExpanded, setConfigMenuOpen } = useShell();

  const navItems = useMemo(
    () => [
      {
        href: registryRoutes.MEMBERS,
        label: navigationMessages['navigation.menuEntry.members.label'],
        selected: pathname.startsWith(registryRoutes.MEMBERS),
        startIcon: <LeadingIcon />,
      },
      {
        href: registryRoutes.ANIMALS,
        label: navigationMessages['navigation.menuEntry.animals.label'],
        selected: pathname.startsWith(registryRoutes.ANIMALS),
        startIcon: <AnimalsIcon />,
      },
      {
        href: registryRoutes.IMPORTS,
        label: navigationMessages['navigation.menuEntry.imports.label'],
        selected: pathname.startsWith(registryRoutes.IMPORTS),
        startIcon: <UploadFileIcon />,
      },
      ...(canViewAudit
        ? [
            {
              href: registryRoutes.AUDIT,
              label: navigationMessages['navigation.menuEntry.audit.label'],
              selected: pathname.startsWith(registryRoutes.AUDIT),
              startIcon: <HistoryIcon />,
            },
          ]
        : []),
      {
        expanded: configMenuOpen,
        href: '',
        label: navigationMessages['navigation.menuEntry.configurations.label'],
        selected:
          pathname.startsWith(registryRoutes.FIELDS_CONFIG) ||
          pathname.startsWith(registryRoutes.FORMS_MANANGER) ||
          pathname.startsWith(registryRoutes.SOCIETY_SETTINGS) ||
          pathname.startsWith(registryRoutes.CERTIFICATE_TEMPLATES) ||
          pathname.startsWith(registryRoutes.RULES) ||
          pathname.startsWith(registryRoutes.IMPORT_HUB),
        startIcon: <AdjustmentsVerticalIcon />,
        endIcon: sidebarExpanded ? (
          <Box>{configMenuOpen ? <ExpandMoreIcon /> : <ExpandLessIcon />}</Box>
        ) : undefined,
        subnavs: [
          {
            href: registryRoutes.FIELDS_CONFIG,
            label:
              navigationMessages[
                'navigation.menuEntry.form-builder.fields-config.label'
              ],
            selected: pathname.startsWith(registryRoutes.FIELDS_CONFIG),
            onClick: () => !sidebarExpanded && setConfigMenuOpen(false),
          },
          {
            href: registryRoutes.FORMS_MANANGER,
            label:
              navigationMessages[
                'navigation.subMenu.configurations.formsManager.label'
              ],
            selected: pathname.startsWith(registryRoutes.FORMS_MANANGER),
            onClick: () => !sidebarExpanded && setConfigMenuOpen(false),
          },
          {
            href: registryRoutes.CERTIFICATE_TEMPLATES,
            label:
              navigationMessages[
                'navigation.subMenu.configurations.certificateTemplates.label'
              ],
            selected: pathname.startsWith(registryRoutes.CERTIFICATE_TEMPLATES),
            onClick: () => !sidebarExpanded && setConfigMenuOpen(false),
          },
          {
            href: registryRoutes.RULES,
            label:
              navigationMessages[
                'navigation.subMenu.configurations.rules.label'
              ],
            selected: pathname.startsWith(registryRoutes.RULES),
            onClick: () => !sidebarExpanded && setConfigMenuOpen(false),
          },
          ...(canManageSocietySettings
            ? [
                {
                  href: registryRoutes.SOCIETY_SETTINGS,
                  label:
                    navigationMessages[
                      'navigation.subMenu.configurations.logo.label'
                    ],
                  selected: pathname.startsWith(
                    registryRoutes.SOCIETY_SETTINGS,
                  ),
                  onClick: () => !sidebarExpanded && setConfigMenuOpen(false),
                },
              ]
            : []),
          ...(canImportData
            ? [
                {
                  href: registryRoutes.IMPORT_HUB,
                  label:
                    navigationMessages[
                      'navigation.subMenu.configurations.import.label'
                    ],
                  selected: pathname.startsWith(registryRoutes.IMPORT_HUB),
                  onClick: () => !sidebarExpanded && setConfigMenuOpen(false),
                },
              ]
            : []),
        ],
        onClick: (event: SyntheticEvent) => {
          event.preventDefault();
          setConfigMenuOpen(!configMenuOpen);
        },
      },
    ],
    [
      canImportData,
      canManageSocietySettings,
      canViewAudit,
      configMenuOpen,
      pathname,
      registryRoutes,
      sidebarExpanded,
      setConfigMenuOpen,
    ],
  );

  return <ShellNavItems items={navItems} />;
}
